import { useCallback, useMemo, useState } from 'react'
import type { ChangeEvent, SetStateAction } from 'react'

import { isEqualMonth, isEqualYear } from '@/utils/date'

import type { NewsRelease } from '@/types/newsRelease'

export const useNewsList = (
  newsList: NewsRelease[],
  countPerPage: number,
  initialTag?: string | undefined
) => {
  const [selectedYear, setSelectedYear] = useState('')
  const [selectedMonth, setSelectedMonth] = useState('')
  const [selectedTags, setSelectedTags] = useState<readonly string[]>(
    initialTag ? [initialTag] : []
  )
  const [currentPage, setCurrentPage] = useState(1)

  const filteredNewsList = useMemo(
    () =>
      newsList.filter(({ tag, publishDate }) => {
        const yearFilter = selectedYear
          ? isEqualYear(publishDate, selectedYear)
          : true
        const monthFilter = selectedMonth
          ? isEqualMonth(publishDate, selectedMonth)
          : true
        const tagFilter =
          selectedTags.length > 0
            ? tag
                .map(({ name }) => name)
                .some((item) => selectedTags.includes(item))
            : true

        return yearFilter && monthFilter && tagFilter
      }),

    [newsList, selectedMonth, selectedTags, selectedYear]
  )

  const pickupNewsList = useMemo(
    () => newsList.filter(({ isPickupNews }) => isPickupNews),
    [newsList]
  )

  const displayNewsList = useMemo(() => {
    const start = countPerPage * (currentPage - 1)
    const end = start + countPerPage
    const list = filteredNewsList.slice(
      start,
      filteredNewsList.length < end ? undefined : end
    )

    // 重要なお知らせは常に先頭に表示する
    return Array.from(new Set([...pickupNewsList, ...list]))
  }, [currentPage, filteredNewsList, countPerPage, pickupNewsList])

  const changeSelectedYear = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setSelectedYear(event.target.value)
      setCurrentPage(1)
    },
    []
  )

  const changeSelectedMonth = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setSelectedMonth(event.target.value)
      setCurrentPage(1)
    },
    []
  )

  const changeSelectedTag = useCallback((text: string) => {
    setSelectedTags((currentTags) => {
      if (!currentTags) {
        return [text]
      }

      if (currentTags.includes(text)) {
        return currentTags.filter((tag) => tag !== text)
      }

      return [...currentTags, text]
    })
    setCurrentPage(1)
  }, [])

  const handleAllTags = useCallback(() => {
    setSelectedTags([])
    setCurrentPage(1)
  }, [])

  const transitionPage = useCallback((value: SetStateAction<number>) => {
    setCurrentPage(value)
    window.scrollTo({ top: 0 })
  }, [])

  return {
    selectedTags,
    displayNewsList,
    totalCount: filteredNewsList.length,
    currentPage,
    changeSelectedYear,
    changeSelectedMonth,
    changeSelectedTag,
    transitionPage,
    handleAllTags,
  }
}
